import { ChevronDownIcon } from "@radix-ui/react-icons";
import { Check, Loader2, View } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  useWebsiteChannels,
  type WebsiteChannel,
} from "@/hooks/api/use-website-channels";
import { cn } from "@/lib/utils";

interface ChannelSelectorProps {
  selectedChannelId?: string;
  onChannelChange?: (channelId: string) => void;
  className?: string;
  webpageUrl?: string;
}

export function ChannelSelector({
  selectedChannelId = "all",
  onChannelChange,
  className,
  webpageUrl,
}: ChannelSelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch channels from the backend
  const { data: channelsData, isLoading: isLoadingChannels } =
    useWebsiteChannels(webpageUrl);

  const channels = channelsData?.channels || [];

  // Normalize channel data to handle both uppercase and lowercase property names
  const normalizedChannels = channels.map(
    (
      channel: WebsiteChannel & {
        CHANNEL?: string;
        PAGE_VIEW_COUNT?: number | string;
      },
    ) => ({
      channel: channel.channel || channel.CHANNEL || "",
      page_view_count: channel.page_view_count || channel.PAGE_VIEW_COUNT || 0,
    }),
  );

  // Filter channels based on search query
  const filteredChannels = normalizedChannels.filter((channel) =>
    channel.channel?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // Find the currently selected channel
  const selectedChannel = normalizedChannels.find(
    (channel) => channel.channel === selectedChannelId,
  );

  // Handle channel selection
  const handleChannelSelect = (channelId: string) => {
    onChannelChange?.(channelId);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-testid="webpage-channel-selector-trigger"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "h-8 px-3 text-xs justify-between max-w-full min-w-0",
            className,
          )}
          disabled={isLoadingChannels}
        >
          {isLoadingChannels ? (
            <div className="flex items-center space-x-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Loading channels...</span>
            </div>
          ) : selectedChannel ? (
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <View className="h-4 w-4" />
              <span className="text-muted-foreground">Channel:</span>
              <span
                data-testid="webpage-channel-selector-selected-channel"
                className="truncate text-primary"
              >
                {selectedChannel.channel === "all"
                  ? "All Channels"
                  : selectedChannel.channel}
              </span>
            </div>
          ) : (
            <span className="flex items-center gap-2">
              <View className="h-4 w-4" />
              <span className="text-muted-foreground">Channel:</span>
              <span className="text-destructive">no channel selected</span>
            </span>
          )}
          <ChevronDownIcon className="h-4 w-4 shrink-0 opacity-50 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="start">
        <Command>
          <CommandInput
            placeholder="Search channels..."
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            <CommandEmpty>
              {isLoadingChannels ? (
                <div className="flex items-center justify-center space-x-2 py-4">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Loading channels...</span>
                </div>
              ) : (
                "No channels found."
              )}
            </CommandEmpty>
            <CommandGroup>
              {filteredChannels.map((channel) => (
                <CommandItem
                  key={channel.channel}
                  value={channel.channel}
                  onSelect={() => handleChannelSelect(channel.channel)}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center space-x-2">
                    <Check
                      className={cn(
                        "h-4 w-4",
                        selectedChannelId === channel.channel
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                    <span className="truncate">
                      {channel.channel === "all"
                        ? "All Channels"
                        : channel.channel}
                    </span>
                  </div>
                  {channel.channel !== "all" && (
                    <span className="text-xs text-muted-foreground ml-2">
                      {typeof channel.page_view_count === "number"
                        ? `${channel.page_view_count.toLocaleString()} views`
                        : channel.page_view_count}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
